import { Entity, Hit, noHit } from "./Entity.ts"
import { Ray, vec3 } from "./VectorMath.ts"

/**
 * Consume each sequence of three positions from `positions` to construct a triangle, and return the earliest `Hit` of `ray` hitting any of those triangles.
 * @param who The `Entity` to put in the `Hit` I return (if I don't return `noHit`).
 * @param positions A sequence of positions.
 * @param ray A ray.
 * @returns The `Hit` with the earliest time at which `ray` intersects any of the triangles. If `ray` doesn't hit any triangle (including if `positions` doesn't contain at least three positions), I return `noHit`. If I return a real `Hit`, its `extra` is the index of the triangle that was hit (ranging from zero to one less than the number of triangles I tested).
 */
export function meshHitTest(who: Entity | null, positions: Iterator<vec3>, ray: Ray): Hit {
  let tMin = Infinity
  let iMin = -1
  for (let i = 0; ; ++i) {
    const t = triangleHitTest(positions, ray)
    if (t === null) {
      break
    }
    if (t < tMin) {
      tMin = t
      iMin = i
    }
  }

  if (iMin === -1) {
    return noHit
  }

  return {
    who,
    when: tMin,
    where: ray.at(tMin),
    extra: iMin
  }
}

/**
 * Consume the next three values of `positions` to construct a triangle, and return the time at which `ray` intersects that triangle.
 * @param positions A sequence of positions.
 * @param ray A ray.
 * @returns The time `ray` intersects the triangle. If `ray` does not intersect the triangle, I return `Infinity`. If I can't pull three positions from `positions`, I return `null`.
 */
function triangleHitTest(positions: Iterator<vec3>, ray: Ray): number | null {
  const vs = next3(positions)
  if (!vs) {
    return null
  }

  const [v0, v1, v2] = vs

  // Ported from C intersect_triangle1 by Tomas Möller
  // https://fileadmin.cs.lth.se/cs/Personal/Tomas_Akenine-Moller/raytri/
  const edge1 = v1.minus(v0)
  const edge2 = v2.minus(v0)

  const pvec = ray.direction.cross(edge2)
  const det = edge1.dot(pvec)

  const epsilon = 1e-6

  let u: number
  let v: number

  if (det > epsilon) {
    const tvec = ray.origin.minus(v0)
    u = tvec.dot(pvec)
    if (u < 0 || u > det) {
      return Infinity
    }

    const qvec = tvec.cross(edge1)
    v = ray.direction.dot(qvec)
    if (v < 0 || u + v > det) {
      return Infinity
    }

    return edge2.dot(qvec) / det
  } else if (det < -epsilon) {
    const tvec = ray.origin.minus(v0)
    u = tvec.dot(pvec)
    if (u > 0 || u < det) {
      return Infinity
    }

    const qvec = tvec.cross(edge1)
    const v = ray.direction.dot(qvec)
    if (v > 0 || u + v < det) {
      return Infinity
    }

    const t = edge2.dot(qvec) / det
    return t <= 0 ? Infinity : t
  } else {
    // ray is nigh-parallel to the supporting plane.
    return Infinity
  }
}

function next3<T>(iterator: Iterator<T>): [T, T, T] | null {
  const r0 = iterator.next()
  if (r0.done) {
    return null
  }
  const r1 = iterator.next()
  if (r1.done) {
    return null
  }
  const r2 = iterator.next()
  if (r2.done) {
    return null
  }
  return [r0.value, r1.value, r2.value]
}

